import React from 'react';
import { Box, Button, Chip, Typography } from '@mui/material';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import SecurityOutlinedIcon from '@mui/icons-material/SecurityOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined';
import dayjs from 'dayjs';
import useLicenseSx from '../hooks/useLicenseSx';
import {
  formatPlanPrice,
  getAiCreditsLabel,
  getDurationLabel,
  getUsersLabel,
} from '../utils/normalizeLicense';

const STATUS_LABELS = {
  active: 'Active',
  draft: 'Draft',
  inactive: 'Inactive',
  archived: 'Archived',
};

function getStatusSx(status, styles) {
  if (status === 'inactive' || status === 'archived') return styles.statusExpired;
  if (status === 'draft') return styles.statusExpiring;
  return styles.statusActive;
}

export default function LicenseCard({ license, onEdit, onArchive }) {
  const styles = useLicenseSx();
  const statusSx = getStatusSx(license.status, styles);
  const profileName = license.permissionProfile?.name;
  const aiCreditsFeature = (license.featureSummary || []).find((feature) => feature.key === 'ai_credits');
  const usersFeature = (license.featureSummary || []).find((feature) => feature.key === 'users');
  const isArchived = license.status === 'archived';

  return (
    <Box sx={styles.card}>
      <Box sx={styles.cardHeader}>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="h6" fontWeight={700} noWrap title={license.alias || license.name}>
            {license.alias || license.name}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
            {license.name}
          </Typography>
          {license.code && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              Code: {license.code}
            </Typography>
          )}
          {license.description && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                mt: 0.5,
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
            >
              {license.description}
            </Typography>
          )}
        </Box>
        <Chip
          label={STATUS_LABELS[license.status] || license.status || 'Active'}
          size="small"
          sx={[styles.statusChip, statusSx]}
        />
      </Box>

      <Box sx={styles.cardBody}>
        <Box sx={{ ...styles.metaRow, mb: 1 }}>
          <CalendarTodayOutlinedIcon sx={{ fontSize: 16 }} />
          <span>{getDurationLabel(license.durationMonths)}</span>
        </Box>
        <Box sx={{ ...styles.metaRow, mb: 1 }}>
          <PeopleOutlineIcon sx={{ fontSize: 16 }} />
          <span>{getUsersLabel(license.maxUsers)}</span>
        </Box>
        <Box sx={{ ...styles.metaRow, mb: 1 }}>
          <AutoAwesomeOutlinedIcon sx={{ fontSize: 16 }} />
          <span>{getAiCreditsLabel(license.aiCredits)}</span>
        </Box>
        {profileName && (
          <Box sx={{ ...styles.metaRow, mb: 1 }}>
            <SecurityOutlinedIcon sx={{ fontSize: 16 }} />
            <span>{profileName}</span>
          </Box>
        )}
        <Box sx={styles.metaRow}>
          <PaymentsOutlinedIcon sx={{ fontSize: 16 }} />
          <span>{formatPlanPrice(license.price, license.durationMonths, license.billingInterval)}</span>
        </Box>

        {(usersFeature || aiCreditsFeature) && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 2 }}>
            {usersFeature && (
              <Chip
                label={`Users: ${getUsersLabel(license.maxUsers)}`}
                size="small"
                variant="outlined"
                sx={{ height: 24, fontSize: '0.7rem' }}
              />
            )}
            {aiCreditsFeature && (
              <Chip
                label={`AI Credits: ${getAiCreditsLabel(license.aiCredits)}`}
                size="small"
                variant="outlined"
                sx={{ height: 24, fontSize: '0.7rem' }}
              />
            )}
          </Box>
        )}
      </Box>

      <Box sx={styles.cardFooter}>
        <Typography variant="caption" color="text.secondary">
          Org sees: {license.alias || '—'}
        </Typography>
        {license.createdAt && (
          <Typography variant="caption" color="text.secondary">
            Created {dayjs(license.createdAt).format('DD MMM YYYY')}
          </Typography>
        )}
      </Box>

      <Box sx={styles.cardActions}>
        <Button
          size="small"
          startIcon={<EditOutlinedIcon sx={{ fontSize: 16 }} />}
          onClick={() => onEdit?.(license)}
          disabled={isArchived}
          sx={styles.cardActionButton}
        >
          Edit
        </Button>
        <Button
          size="small"
          color="warning"
          startIcon={<ArchiveOutlinedIcon sx={{ fontSize: 16 }} />}
          onClick={() => onArchive?.(license)}
          disabled={isArchived}
          sx={styles.cardActionButton}
        >
          Archive
        </Button>
      </Box>
    </Box>
  );
}
